import request from '@/scripts/request';
import type { MyAxiosResponseData } from '@/scripts/request/types';
import { sendGroupSetDB } from '@/scripts/dataBase';
import type {
  ChatGroupGroupId,
  ChatGroupSettingsParams,
  ChatGroupSettingsPropArgs,
} from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { genChatMember } from '@/contexts/chat/scripts/hooks/useChatGroup/utils';
import { getAvatar } from '@/scripts/utils/genAvatar';
import { ActionType } from '@/scripts/dataBase/type';
import { exportApis } from '@/scripts/utils/devUtils';
import { defaultMsgExpireDuration } from '@/configs/options';
import type {
  CreateGroupParams,
  GroupEncryptCommitsParams,
  GroupEncryptInfoParams,
  GroupEncryptMembersParams,
  GroupInfo,
  GroupMemberList,
  GroupSettings,
  RefreshGroupEncryptInfoParams,
  UpdateGroupInfoParams,
  UpdateUserkeyPackageParams,
} from './interface';
import { membersApis } from './members';
import { readStatusApis } from './readStatus';
import { groupApis } from './groups';
import { encryptionDBApis } from './encryption';

export const groupsApis = {
  createGroup(params: CreateGroupParams) {
    return request<MyAxiosResponseData<{ gid: string; name: string }>>(
      '/api/v1/group/info',
      {
        method: 'POST',
        data: params,
      }
    ).then((res) => res.data);
  },
  getGroupInfo(gid: string) {
    return request<MyAxiosResponseData<GroupInfo>>(`/api/v1/group/info`, {
      params: { gid },
    }).then((res) => (res.data?.gid ? res.data : undefined));
  },
  getGroupSettings(gid: string) {
    return request<MyAxiosResponseData<GroupSettings>>(`/api/v1/group/setting`, {
      params: { gid },
    }).then((res) => {
      res.data.expire_duration = res.data.expire_duration || defaultMsgExpireDuration;
      return res.data;
    });
  },
  updateGroupSettings(params: Partial<GroupSettings>) {
    return request(`/api/v1/group/setting/map`, {
      method: 'PUT',
      data: params,
    }).then((res) => res.data);
  },
  getGroupMemberList(gid: string) {
    return request<MyAxiosResponseData<{ member_list: GroupMemberList[] }>>(
      `/api/v1/group/member`,
      {
        params: { gid },
      }
    ).then((res) =>
      res.data.member_list?.map((item) =>
        genChatMember({
          user_id: item.member_uid,
          role: item.group_position,
          avatar: getAvatar(item.avatar, item.user_name),
          name: item.user_name,
          username: item.user_username,
        })
      )
    );
  },
  inviteMembers(gid: string, invite_uid_list: string[]) {
    return request(`/api/v1/group/member`, {
      method: 'POST',
      data: { gid, invite_uid_list },
    }).then((res) => res.data);
  },
  joinGroup(gid: string) {
    return request(`/api/v1/group/join`, {
      method: 'POST',
      data: { gid },
    }).then((res) => res.data);
  },
  removeMembers(gid: string, uids: string[]) {
    return request<MyAxiosResponseData<{ remove_fail_list: string[] }>>(
      `/api/v1/group/member`,
      {
        method: 'DELETE',
        data: { gid, uids },
      }
    ).then((res) => res.data);
  },
  updateGroupInfo(params: UpdateGroupInfoParams) {
    return request(`/api/v1/group/info`, {
      method: 'PUT',
      data: params,
    }).then((res) => res.data);
  },
  exitGroup(gids: string[]) {
    return request<MyAxiosResponseData<{ quit_fail_list: string[] }>>(
      `/api/v1/group/quit`,
      {
        method: 'POST',
        data: { gids },
      }
    ).then((res) => res.data);
  },
  dismissGroup(gids: string[]) {
    return request(`/api/v1/group/disband`, {
      method: 'DELETE',
      data: { gids },
    }).then((res) => res.data);
  },
  updateMemberkeyPackage(params: UpdateUserkeyPackageParams) {
    return request(`/api/v1/group/keyPackage`, {
      method: 'PUT',
      data: params,
    });
  },
  getMemberskeyPackages(params: string[]) {
    return request<
      MyAxiosResponseData<{ uid: string; key: string; hash_ref: string }[]>
    >(`/api/v1/group/keyPackages`, {
      method: 'POST',
      data: params,
    }).then((res) => res.data);
  },
  updateGroupEncryptInfo(params: GroupEncryptInfoParams) {
    return request(`/api/v1/group/encryptInfo`, {
      method: 'POST',
      data: params,
    });
  },
  getGroupEncryptInfo(groupId: string) {
    return request<MyAxiosResponseData<GroupEncryptInfoParams>>(
      `/api/v1/group/encryptInfo/${groupId}`
    ).then((res) => res.data);
  },
  refreshGroupEncryptInfo(params: RefreshGroupEncryptInfoParams) {
    return request(`/api/v1/group/encrypt/refresh`, {
      method: 'POST',
      data: params,
      disableErrorMsg: true,
    }).then((res) => res.data);
  },
  addGroupEncryptMembers(params: GroupEncryptMembersParams) {
    return request(`/api/v1/group/encrypt/member`, {
      method: 'POST',
      data: params,
      disableErrorMsg: true,
    });
  },
  joinEncryptGroup(gid: string, epoch: number, data: string, commit: string) {
    return request(`/api/v1/group/encrypt/join`, {
      method: 'POST',
      data: { gid, epoch, data, commit },
    }).then((res) => res.data);
  },
  removeGroupEncryptMembers(params: GroupEncryptMembersParams) {
    return request<MyAxiosResponseData<{ remove_fail_list: string[] }>>(
      `/api/v1/group/encrypt/member`,
      {
        method: 'DELETE',
        data: params,
        disableErrorMsg: true,
      }
    ).then((res) => res.data);
  },
  exitEncryptGroup(gids: string[]) {
    return request<MyAxiosResponseData<{ quit_fail_list: string[] }>>(
      `/api/v1/group/encrypt/quit`,
      {
        method: 'POST',
        data: { gids },
        disableErrorMsg: true,
      }
    ).then((res) => res.data);
  },
  groupMediaCalling(gids: string[]) {
    return request<
      MyAxiosResponseData<{
        list: { room_id: string; gid: string; type: number }[];
      }>
    >(`/api/v1/group/media/calling`, {
      method: 'POST',
      data: { gids },
    }).then((res) => res.data);
  },
  getGroupIds() {
    return request<
      MyAxiosResponseData<{ gids: string[]; encryption: string[]; unencrypted: [] }>
    >(`/api/v1/group/ids`).then((res) => res.data);
  },
  getGroupEncryptInfos(params: string[]) {
    return request<MyAxiosResponseData<GroupEncryptInfoParams[]>>(
      `/api/v1/group/encryptInfos`,
      {
        method: 'POST',
        data: params,
      }
    ).then((res) => res.data);
  },
  getGroupCurrentEpoch(params: string[]) {
    return request<MyAxiosResponseData<Record<string, number>>>(
      `/api/v1/group/encrypt/currentEpoch`,
      {
        method: 'POST',
        data: params,
      }
    ).then((res) => res.data);
  },
  getGroupEncryptCommits(params: { gid: string; epochs: number[] }) {
    return request<MyAxiosResponseData<GroupEncryptCommitsParams[]>>(
      `/api/v1/group/encrypt/commits`,
      {
        method: 'POST',
        data: params,
      }
    ).then((res) => res.data);
  },
};

export const groupDB = {
  ...encryptionDBApis,
  ...groupApis,
  ...membersApis,
  ...readStatusApis,
  getSettings(id: ChatGroupGroupId) {
    return sendGroupSetDB(ActionType.getGroupSetDatas, {
      tableName: id,
    });
  },
  insertSettings(args: ChatGroupSettingsParams[]) {
    return Promise.all(
      args.map(({ group_id, ...params }) =>
        Promise.all(
          Object.entries(params).map(([key, value]) =>
            sendGroupSetDB(ActionType.setGroupSetData, {
              tableName: group_id,
              params: { key, value },
            })
          )
        )
      )
    );
  },
  removeSettings(args: ChatGroupSettingsPropArgs[]) {
    return Promise.all(
      args.map(({ id, args }) =>
        Promise.all(
          args.map((prop) =>
            sendGroupSetDB(ActionType.setGroupSetData, {
              tableName: id,
              params: prop,
            })
          )
        )
      )
    );
  },
  deleteGroupSetTableManager(id: ChatGroupGroupId) {
    return sendGroupSetDB(ActionType.deleteTableManager, {
      tableName: id,
    });
  },
};

exportApis({ groupApis: groupDB });
